import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay, faXmark, faMicrophone } from '@fortawesome/free-solid-svg-icons';
import { cycleVoiceRate, stopVoiceNote, toggleVoiceNote, useVoiceDock } from '../utils/voicePlayback';
import '../styles/audio.css';

const fmt = (seconds) => {
    const total = Math.max(0, Math.round(seconds || 0));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * The voice note that is playing, once you can no longer see it.
 *
 * Playback outlives the bubble (utils/voicePlayback), which on its own leaves a
 * note talking with nothing on screen to say what it is or how to stop it.
 * This floats while a note is loaded - playing, or paused part-way - and none
 * of its bubbles is in view: another chat, another page, or scrolled away.
 *
 * Mounted in the app shell (DashboardLayout) and the external portal, so it
 * survives every navigation that the note does.
 */
const VoiceMiniPlayer = () => {
    const navigate = useNavigate();
    const { url, playing, currentTime, duration, rate, origin, onScreen } = useVoiceDock();

    if (!url || onScreen || (!playing && currentTime <= 0)) return null;

    const progress = duration ? Math.min(1, currentTime / duration) : 0;
    const open = () => {
        if (origin?.href) navigate(origin.href, origin.state ? { state: origin.state } : undefined);
    };

    return (
        <div className="vmp" role="region" aria-label="Voice note playing">
            <button type="button" className="vmp-play" onClick={() => toggleVoiceNote(url)}
                aria-label={playing ? 'Pause voice note' : 'Play voice note'}>
                <FontAwesomeIcon icon={playing ? faPause : faPlay} />
            </button>

            <button type="button" className="vmp-info" onClick={open} disabled={!origin?.href}
                title={origin?.href ? 'Go to this voice note' : undefined}>
                <span className="vmp-title">
                    <FontAwesomeIcon icon={faMicrophone} className="vmp-mic" />
                    {origin?.title || 'Voice message'}
                </span>
                <span className="vmp-sub">
                    {[origin?.subtitle, `${fmt(currentTime)} / ${fmt(duration)}`].filter(Boolean).join(' · ')}
                </span>
            </button>

            <button type="button" className="vmp-rate" onClick={cycleVoiceRate}
                aria-label={`Playback speed ${rate}x. Change speed`}>
                {rate}x
            </button>

            <button type="button" className="vmp-close" onClick={stopVoiceNote} aria-label="Stop and close">
                <FontAwesomeIcon icon={faXmark} />
            </button>

            <div className="vmp-track" aria-hidden="true">
                <div className="vmp-fill" style={{ width: `${progress * 100}%` }} />
            </div>
        </div>
    );
};

export default VoiceMiniPlayer;
